// assets/AllCourses/Exercises/MatchingExercise.jsx
"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function MatchingExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE PAIRS ================= */
  const pairs = Array.isArray(exercise.matchingPairs)
    ? exercise.matchingPairs.map(p => ({
        question:
          typeof p.question === "string" ? p.question.trim() : "",
        answer:
          typeof p.answer === "string" ? p.answer.trim() : "",
        requireCapitalization: Boolean(p.requireCapitalization),
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    matches: {}, // questionIdx -> shuffledAnswerIdx
    hasInteracted: false,
  });

  const [selectedQuestionIdx, setSelectedQuestionIdx] =
    useState(null);

  const completedRef = useRef(false);

  /* ================= SHUFFLE ANSWERS ================= */
  const shuffledAnswers = useMemo(() => {
    return pairs
      .map((p, idx) => ({
        answer: p.answer,
        originalIndex: idx,
      }))
      .sort(() => Math.random() - 0.5);
  }, [exercise._id, isPreview]);

  /* ================= RESTORE ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;

    setState({
      matches:
        typeof exercise.attempt.matches === "object"
          ? exercise.attempt.matches
          : {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });

    setSelectedQuestionIdx(null);
  }, [isPreview, exercise.attempt]);

  useEffect(() => {
    if (isPreview) {
      completedRef.current = false;
      setSelectedQuestionIdx(null);
    }
  }, [exercise._id, isPreview]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT HELPERS ================= */
  const updatePairs = next => {
    if (isEditMode) onUpdate("matchingPairs", next);
  };

  const updatePairField = (idx, field, value) => {
    if (!isEditMode) return;

    const next = [...pairs];
    next[idx] = {
      ...next[idx],
      [field]: value.trim(),
    };

    updatePairs(next);
  };

  /* ================= STUDENT INTERACTION ================= */
  const handleQuestionClick = idx => {
    if (
      isEditMode ||
      showResult ||
      state.matches[idx] !== undefined
    )
      return;

    setSelectedQuestionIdx(idx);

    if (!state.hasInteracted) {
      setState(prev => ({
        ...prev,
        hasInteracted: true,
      }));
    }
  };

  const handleAnswerClick = shuffledIdx => {
    if (
      !isPreview ||
      selectedQuestionIdx === null ||
      showResult
    )
      return;

    setState(prev => {
      const next = {
        ...prev,
        matches: {
          ...prev.matches,
          [selectedQuestionIdx]: shuffledIdx,
        },
        hasInteracted: true,
      };

      setAttempt(next);
      setSelectedQuestionIdx(null);

      const allMatched = pairs.every(
        (_, i) => next.matches[i] !== undefined
      );

      if (allMatched && !completedRef.current) {
        completedRef.current = true;
        checkAnswers(next);
      }

      return next;
    });
  };

  const checkAnswers = currentState => {
    const allCorrect = pairs.every((pair, qIdx) => {
      const shuffledIdx = currentState.matches[qIdx];
      if (shuffledIdx === undefined) return false;

      const userAnswer =
        shuffledAnswers[shuffledIdx]?.answer || "";

      return (
        normalize(userAnswer, pair.requireCapitalization) ===
        normalize(pair.answer, pair.requireCapitalization)
      );
    });

    onComplete(exercise._id, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {pairs.map((p, idx) => (
          <Card key={idx} className="p-6">
            <div className="space-y-4">
              <StringField
                label={`Question ${idx + 1}`}
                value={p.question}
                onChange={v =>
                  updatePairField(idx, "question", v)
                }
                placeholder="Capital of France?"
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-pair-${idx}-q`
                  ]
                }
              />

              <StringField
                label={`Answer ${idx + 1}`}
                value={p.answer}
                onChange={v =>
                  updatePairField(idx, "answer", v)
                }
                placeholder="Paris"
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-pair-${idx}-a`
                  ]
                }
              />

              <CapitalizationRule
                checked={!!p.requireCapitalization}
                onChange={c =>
                  updatePairField(
                    idx,
                    "requireCapitalization",
                    c
                  )
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updatePairs(
                    pairs.filter((_, i) => i !== idx)
                  )
                }
              >
                Remove Pair
              </Button>
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            updatePairs([
              ...pairs,
              {
                question: "",
                answer: "",
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Pair
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!pairs.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No matching pairs available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Match the Pairs
        </CardTitle>
        <CardDescription>
          Click a question, then click its matching answer.
        </CardDescription>
      </CardHeader>

      <CardContent className="grid md:grid-cols-2 gap-8">
        {/* Questions */}
        <div className="space-y-3">
          <h3 className="font-semibold mb-3">Questions</h3>

          {pairs.map((p, qIdx) => {
            const matchedIdx = state.matches[qIdx];
            const userAnswer =
              matchedIdx !== undefined
                ? shuffledAnswers[matchedIdx]?.answer
                : null;

            const isCorrect =
              showResult &&
              userAnswer &&
              normalize(userAnswer, p.requireCapitalization) ===
                normalize(p.answer, p.requireCapitalization);

            return (
              <Button
                key={qIdx}
                variant="outline"
                onClick={() =>
                  handleQuestionClick(qIdx)
                }
                disabled={
                  matchedIdx !== undefined || showResult
                }
                className={cn(
                  "w-full justify-start text-left py-4",
                  selectedQuestionIdx === qIdx &&
                    "bg-accent",
                  matchedIdx !== undefined &&
                    "opacity-60 cursor-not-allowed",
                  showResult &&
                    matchedIdx !== undefined &&
                    isCorrect &&
                    "bg-green-100 border-green-500",
                  showResult &&
                    matchedIdx !== undefined &&
                    !isCorrect &&
                    "bg-red-100 border-red-500"
                )}
              >
                <span className="font-medium mr-2">
                  {qIdx + 1}.
                </span>
                <span>{p.question}</span>

                {showResult && matchedIdx !== undefined && (
                  <span className="ml-auto text-sm">
                    {isCorrect
                      ? "Correct"
                      : `Wrong → ${p.answer}`}
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        {/* Answers */}
        <div className="space-y-3">
          <h3 className="font-semibold mb-3">Answers</h3>

          {shuffledAnswers.map((item, aIdx) => {
            const isUsed = Object.values(
              state.matches
            ).includes(aIdx);

            return (
              <Button
                key={aIdx}
                variant="outline"
                onClick={() =>
                  handleAnswerClick(aIdx)
                }
                disabled={isUsed || showResult}
                className={cn(
                  "w-full justify-start text-left py-4",
                  isUsed &&
                    "opacity-60 cursor-not-allowed"
                )}
              >
                {item.answer}
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
